import { useMemo, useState } from "react";
import { Check, ChevronDown, MapPin } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { COUNTRIES, ZONES } from "@/data/site";

const INDIA = ["Ladakh", "Spiti", "Meghalaya", "Himachal", "Rajasthan"];

export function useDestinationGroups() {
  return useMemo(() => {
    const groups = ZONES.map((z) => ({
      zone: z.slug, label: z.name,
      items: COUNTRIES.filter(([, zone]) => zone === z.slug).map(([name]) => name),
    })).filter((g) => g.items.length);
    const south = groups.find((g) => g.zone === "south-asia");
    if (south) south.items = [...south.items, ...INDIA];
    return groups;
  }, []);
}

export function DestinationPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const groups = useDestinationGroups();
  const pick = (name, zone) => {
    onChange({ name, zone });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="hero-destination-trigger"
          className="flex items-center gap-3 flex-1 min-w-0 px-4 py-2 text-left rounded-full hover:bg-cream/70 transition-colors">
          <MapPin className="h-4 w-4 text-terra shrink-0" />
          <span className="min-w-0">
            <span className="block eyebrow text-[10px] text-stone-600">Where to?</span>
            <span className="block font-display font-bold text-lg truncate">{value?.name || "Anywhere"}</span>
          </span>
          <ChevronDown className={`ml-auto h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={10} data-lenis-prevent
        className="w-[min(92vw,380px)] p-0 rounded-3xl border-ink/10 bg-paper shadow-[0_30px_80px_-30px_rgba(21,17,14,0.45)] overflow-hidden">
        <Command className="bg-paper">
          <CommandInput placeholder={`Search ${COUNTRIES.length}+ countries…`} data-testid="hero-destination-search" />
          <CommandList className="max-h-[360px]">
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup heading="Popular">
              {["Anywhere", "Georgia"].map((n) => (
                <CommandItem key={n} value={`popular ${n}`} onSelect={() => pick(n === "Anywhere" ? "" : n, n === "Georgia" ? "cis-caucasus" : "")}
                  data-testid={`hero-dest-${n.toLowerCase()}`} className="rounded-xl py-2.5 cursor-pointer">
                  <span className={`font-semibold ${n === "Georgia" ? "text-wine" : ""}`}>{n}</span>
                  {n === "Georgia" && <span className="ml-2 eyebrow text-[10px] rounded-full bg-gold/40 px-2 py-0.5">No. 1</span>}
                  {(value?.name || "Anywhere") === n && <Check className="ml-auto h-4 w-4 text-terra" />}
                </CommandItem>
              ))}
            </CommandGroup>
            {groups.map((g) => (
              <CommandGroup key={g.zone} heading={g.label}>
                {g.items.map((n) => (
                  <CommandItem key={`${g.zone}-${n}`} value={`${g.label} ${n}`} onSelect={() => pick(n, g.zone)}
                    data-testid={`hero-dest-${n.toLowerCase().replace(/\s/g, "-")}`} className="rounded-xl py-2 cursor-pointer">
                    {n}
                    {value?.name === n && <Check className="ml-auto h-4 w-4 text-terra" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
